// /app/frontend/src/components/compliance/map/initialElements.js
// Sample data flow elements demonstrating a typical customer data pipeline

export const initialNodes = [
  // Data Subjects
  {
    id: 'subjects-1',
    type: 'entityNode',
    position: { x: 50, y: 200 },
    data: {
      id: 'subjects-1',
      type: 'subjects',
      label: 'Customers',
      description: 'Individual customers providing personal data through various channels',
      risk: 'low',
      dataCategories: ['Name', 'Email', 'Phone', 'Address'],
      legalBasis: 'Contract',
      purpose: 'Service delivery and customer relationship management'
    }
  },
  
  // Data Sources
  {
    id: 'source-1',
    type: 'entityNode',
    position: { x: 300, y: 50 },
    data: {
      id: 'source-1',
      type: 'source',
      label: 'Website Forms',
      description: 'Contact forms, registration forms, and inquiry submissions',
      risk: 'medium',
      riskScore: 45,
      dataCategories: ['Name', 'Email', 'Phone', 'Message Content'],
      legalBasis: 'Consent',
      securityMeasures: ['HTTPS Encryption', 'CAPTCHA', 'Input Validation'],
      retention: '2 years after last interaction'
    }
  },
  {
    id: 'source-2',
    type: 'entityNode',
    position: { x: 300, y: 200 },
    data: {
      id: 'source-2',
      type: 'source',
      label: 'Mobile App',
      description: 'iOS and Android applications collecting user data',
      risk: 'medium',
      riskScore: 52,
      dataCategories: ['Name', 'Email', 'Device ID', 'Location', 'Usage Data'],
      legalBasis: 'Consent',
      securityMeasures: ['App Transport Security', 'Data Encryption', 'Biometric Auth'],
      retention: 'Duration of account + 1 year'
    }
  },
  {
    id: 'source-3',
    type: 'entityNode',
    position: { x: 300, y: 350 },
    data: {
      id: 'source-3',
      type: 'source',
      label: 'Customer Service',
      description: 'Phone calls, emails, and chat interactions with support team',
      risk: 'low',
      riskScore: 30,
      dataCategories: ['Name', 'Email', 'Phone', 'Support Tickets'],
      legalBasis: 'Contract',
      securityMeasures: ['Call Recording Notification', 'Secure Email Gateway'],
      retention: '5 years for regulatory compliance'
    }
  },
  
  // Processing Systems
  {
    id: 'process-1',
    type: 'entityNode',
    position: { x: 600, y: 125 },
    data: {
      id: 'process-1',
      type: 'process',
      label: 'CRM System',
      description: 'Central customer relationship management platform',
      risk: 'medium',
      riskScore: 55,
      riskFactors: ['High data volume', 'Multiple integrations'],
      dataCategories: ['Name', 'Email', 'Phone', 'Address', 'Purchase History', 'Interactions'],
      legalBasis: 'Contract',
      securityMeasures: ['Role-Based Access', 'Audit Logging', 'Encryption at Rest'],
      retention: '7 years after account closure',
      ropaId: 'ropa-crm-001'
    }
  },
  {
    id: 'process-2',
    type: 'entityNode',
    position: { x: 600, y: 300 },
    data: {
      id: 'process-2',
      type: 'process',
      label: 'Analytics Engine',
      description: 'Data analytics and business intelligence platform',
      risk: 'high',
      riskScore: 72,
      riskFactors: ['Profiling', 'Automated decisions', 'Large-scale processing'],
      dataCategories: ['Usage Patterns', 'Behavioral Data', 'Aggregated Metrics'],
      legalBasis: 'Legitimate Interest',
      purpose: 'Business intelligence and service improvement',
      securityMeasures: ['Pseudonymization', 'Access Controls', 'Data Minimization'],
      retention: 'Aggregated data: 3 years'
    }
  },
  
  // Storage
  {
    id: 'store-1',
    type: 'entityNode',
    position: { x: 900, y: 200 },
    data: {
      id: 'store-1',
      type: 'store',
      label: 'Primary Database',
      description: 'Main PostgreSQL database storing customer records',
      risk: 'high',
      riskScore: 68,
      riskFactors: ['Contains sensitive data', 'Central repository'],
      dataCategories: ['All Customer PII', 'Transaction Records', 'Consent Records'],
      securityMeasures: ['Encryption at Rest', 'Encrypted Backups', 'Network Isolation', 'MFA Required'],
      retention: 'Per data category policy'
    }
  },
  
  // Third Parties
  {
    id: 'thirdparty-1',
    type: 'entityNode',
    position: { x: 900, y: 50 },
    data: {
      id: 'thirdparty-1',
      type: 'thirdparty',
      label: 'Email Service Provider',
      description: 'SendGrid - transactional and marketing email delivery',
      risk: 'medium',
      riskScore: 48,
      dataCategories: ['Email Address', 'Name', 'Email Content'],
      legalBasis: 'Contract / Consent for marketing',
      securityMeasures: ['DPA in Place', 'SOC 2 Certified', 'Encryption in Transit'],
      retention: '30 days email logs'
    }
  },
  {
    id: 'thirdparty-2',
    type: 'entityNode',
    position: { x: 900, y: 350 },
    data: {
      id: 'thirdparty-2',
      type: 'thirdparty',
      label: 'Payment Processor',
      description: 'Stripe - payment processing and fraud detection',
      risk: 'high',
      riskScore: 75,
      riskFactors: ['Financial data', 'PCI-DSS scope'],
      dataCategories: ['Name', 'Card Details', 'Billing Address', 'Transaction Data'],
      legalBasis: 'Contract',
      securityMeasures: ['PCI-DSS Compliant', 'Tokenization', 'Fraud Detection'],
      retention: '7 years for financial records'
    }
  },
  
  // Cross-Border Transfers
  {
    id: 'transfer-1',
    type: 'entityNode',
    position: { x: 1200, y: 125 },
    data: {
      id: 'transfer-1',
      type: 'transfer',
      label: 'US Cloud Backup',
      description: 'AWS S3 backup storage in US-East region',
      risk: 'medium',
      riskScore: 58,
      dataCategories: ['Encrypted Database Backups'],
      country: 'United States',
      hasAdequacy: false,
      safeguards: 'Standard Contractual Clauses (SCCs)',
      securityMeasures: ['End-to-End Encryption', 'Customer-Managed Keys'],
      retention: '90 days rolling backup'
    }
  },
  {
    id: 'transfer-2',
    type: 'entityNode',
    position: { x: 1200, y: 300 },
    data: {
      id: 'transfer-2',
      type: 'transfer',
      label: 'UK Analytics',
      description: 'Google Analytics 4 data processing',
      risk: 'low',
      riskScore: 35,
      dataCategories: ['IP Address (anonymized)', 'Device Info', 'Page Views'],
      country: 'United Kingdom',
      hasAdequacy: true,
      safeguards: 'UK Adequacy Decision',
      securityMeasures: ['IP Anonymization', 'Data Retention Controls']
    }
  }
];

export const initialEdges = [
  // From Data Subjects to Sources
  {
    id: 'e-subjects-source1',
    source: 'subjects-1',
    target: 'source-1',
    type: 'dataFlowEdge',
    data: { label: 'Web Forms' }
  },
  {
    id: 'e-subjects-source2',
    source: 'subjects-1',
    target: 'source-2',
    type: 'dataFlowEdge',
    data: { label: 'App Data' }
  },
  {
    id: 'e-subjects-source3',
    source: 'subjects-1',
    target: 'source-3',
    type: 'dataFlowEdge',
    data: { label: 'Support' }
  },
  
  // From Sources to CRM
  {
    id: 'e-source1-crm',
    source: 'source-1',
    target: 'process-1',
    type: 'dataFlowEdge',
    data: { label: 'Leads' }
  },
  {
    id: 'e-source2-crm',
    source: 'source-2',
    target: 'process-1',
    type: 'dataFlowEdge',
    data: { label: 'Users' }
  },
  {
    id: 'e-source3-crm',
    source: 'source-3',
    target: 'process-1',
    type: 'dataFlowEdge',
    data: { label: 'Tickets' }
  },
  
  // From Sources to Analytics
  {
    id: 'e-source2-analytics',
    source: 'source-2',
    target: 'process-2',
    type: 'dataFlowEdge',
    data: { label: 'Events', dashed: true }
  },
  
  // From CRM to other systems
  {
    id: 'e-crm-store',
    source: 'process-1',
    target: 'store-1',
    type: 'dataFlowEdge',
    data: { label: 'Storage' }
  },
  {
    id: 'e-crm-email',
    source: 'process-1',
    target: 'thirdparty-1',
    type: 'dataFlowEdge',
    data: { label: 'Emails' }
  },
  {
    id: 'e-crm-analytics',
    source: 'process-1',
    target: 'process-2',
    type: 'dataFlowEdge',
    data: { label: 'Reports', dashed: true }
  },
  
  // From CRM to Payment
  {
    id: 'e-crm-payment',
    source: 'process-1',
    target: 'thirdparty-2',
    type: 'dataFlowEdge',
    data: { label: 'Payments' }
  },
  
  // From Storage to Transfers
  {
    id: 'e-store-usbackup',
    source: 'store-1',
    target: 'transfer-1',
    type: 'dataFlowEdge',
    data: { label: 'Backup', dashed: true }
  },
  
  // From Analytics to UK
  {
    id: 'e-analytics-uk',
    source: 'process-2',
    target: 'transfer-2',
    type: 'dataFlowEdge',
    data: { label: 'Analytics', dashed: true }
  }
];

// Node type options for the add node modal
export const nodeTypeOptions = [
  { value: 'subjects', label: 'Data Subjects', description: 'Individuals whose data is processed' },
  { value: 'source', label: 'Data Source', description: 'Where data is collected from' },
  { value: 'process', label: 'Processing System', description: 'Systems that process data' },
  { value: 'store', label: 'Storage', description: 'Where data is stored' },
  { value: 'thirdparty', label: 'Third Party', description: 'External service providers' },
  { value: 'transfer', label: 'Cross-Border Transfer', description: 'International data transfers' }
];

// Common data categories for dropdown
export const dataCategoryOptions = [
  'Name', 'Email', 'Phone', 'Address', 'Date of Birth', 'National ID',
  'Financial Data', 'Health Data', 'Biometric Data', 'Location Data',
  'IP Address', 'Device ID', 'Cookies', 'Usage Data', 'Behavioral Data',
  'Employment Data', 'Education Data', 'Photos', 'Social Media Data'
];

// Legal basis options
export const legalBasisOptions = [
  { value: 'consent', label: 'Consent' },
  { value: 'contract', label: 'Contract' },
  { value: 'legal_obligation', label: 'Legal Obligation' },
  { value: 'vital_interests', label: 'Vital Interests' },
  { value: 'public_task', label: 'Public Task' },
  { value: 'legitimate_interest', label: 'Legitimate Interest' }
];
